import { isContentOwnerSession, isNendaAdminSession } from "./audience";
import { setActingOwner } from "../http/foyer";
import type { AuthMeResponse } from "@shared/types/auth";

/**
 * The content owner a Nenda admin is working as, inside the supplier shell.
 * Kept in `sessionStorage`: a new tab or signing out starts outside every shell.
 */

const KEY = "foyer:acting-owner";

/** True for a session that may open ANY owner's shell: a Nenda admin. */
export function canActForOwners(
  user: AuthMeResponse | null | undefined
): boolean {
  return isNendaAdminSession(user);
}

/** The owner an admin last opened in this tab, if any. */
export function recallActingOwner(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Act for `ownerId` from now on (an admin entering or switching a shell). */
export function enterOwnerShell(ownerId: string): void {
  setActingOwner(ownerId);
  try {
    sessionStorage.setItem(KEY, ownerId);
  } catch {
    // Private mode: the shell still works, it just forgets on reload.
  }
}

/** Stop acting for any owner (back to Nenda, or signed out). */
export function leaveOwnerShell(): void {
  setActingOwner(null);
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored to remove.
  }
}

/**
 * The owner a session acts for the moment it is known: a supplier's own
 * owner, the owner an admin had open in this tab, or none.
 */
export function actingOwnerFor(
  user: AuthMeResponse | null | undefined
): string | null {
  if (isContentOwnerSession(user)) return user!.access!.homeCompanyId;
  if (canActForOwners(user)) return recallActingOwner();
  return null;
}
